import { normal } from 'color-blend';
import { rgba } from 'polished';
import { DefaultTheme } from 'styled-components';

/**
 * An object representing an RGB color
 */
export interface RGB {
  /**
   * Red channel as an integer from 0 to 255
   */
  r: number;
  /**
   * Green channel as an integer from 0 to 255
   */
  g: number;
  /**
   * Blue channel as an integer from 0 to 255
   */
  b: number;
}

/**
 * An object representing an RGB color with an additional alpha channel
 */
export interface RGBA extends RGB {
  /**
   * Alpha channel as a fraction from 0 to 1
   */
  a: number;
}

export const rgbaStringToChannels = (color: string) => {
  const res = color.match(/[+-]?([0-9]*[.])?[0-9]+/g);

  if (!res) {
    throw new Error(`Bad rgba format ${color}}`);
  }

  const [r, g, b, a] = res.map(Number);
  return { r, g, b, a: a ?? 1 };
};

export function hexToChannels(hex: string) {
  let c;

  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length == 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');

    // @ts-ignore
    const [r, g, b] = [(c >> 16) & 255, (c >> 8) & 255, c & 255];
    return { r, g, b, a: 1 };
  }
  throw new Error(`Bad Hex ${hex}`);
}

const rgbaToString = (obj: RGBA) => {
  return `rgba(${obj.r}, ${obj.g}, ${obj.b}, ${obj.a ?? 1})`;
};

export const colorToChannels = (color: string) => {
  let transform: (c: string) => RGBA;
  if (color === 'transparent') {
    return { r: 0, g: 0, b: 0, a: 0 };
  } else if (color.startsWith('#')) {
    transform = hexToChannels;
  } else {
    transform = rgbaStringToChannels;
  }

  return transform(color);
};

export const createOverlayColorBase = (
  color: string,
  state: keyof DefaultTheme['color']['state'],
  colors: DefaultTheme['color'],
) => {
  return rgbaToString(normal(colorToChannels(color), colorToChannels(colors.state[state])));
};

/*
 * Colors
 * v1.0
 * 31.08.2022
 */

const colors = {
  neutral: {
    100: '#ffffff',
    95: '#F5F5F7',
    90: '#EBECF0',
    80: '#D2D4DF',
    70: '#ADB2C4',
    60: '#8E93AF',
    50: '#686F92',
    40: '#596082',
    30: '#44495F',
    20: '#2A2D3C',
    10: '#15171E',
  },

  primary: {
    90: '#E9F9FB',
    60: '#6AD5E5',
    40: '#20A3B6',
    30: '#177482',
    20: '#0F4E57',
  },

  secondary: {
    90: '#EAECF5',
    80: '#CFD3E8',
    60: '#8691C6',
    50: '#4F5EA6',
    30: '#333D6C',
    10: '#181E34',
  },

  accent: {
    90: '#FFDAEA',
    60: '#FF4496',
    40: '#AF4E77',
    30: '#903C5F',
  },

  surface: {
    background: '#F5F5F7',
    baseline: rgba('#ffffff', 1),
    elevation1: rgba('#44495F', 0.05),
    elevation2: rgba('#44495F', 0.12),
    inverse: '#181E34',
    variant: rgba('#333D6C', 0.06),
    primary: '#6AD5E5',
    modal: '#ffffff',
    error: rgba('#FFF3F5', 1),
    success: '#EDF7F1',
    warning: '#FEF5E7',
    info: '#E1F7FA',
    backdrop: rgba('#333D6C', 0.65),
    backgroundDark: '#333D6C',
    variantDark: rgba('#ffffff', 0.1),
    errorDark: rgba('#E61937', 0.15),
    secondary: rgba('#333D6C', 0.1),
    secondaryDark: rgba('#ffffff', 0.2),
    loader: rgba('#333D6C', 0.3),
    loaderDark: rgba('#FFFFFF', 0.5),
  },

  solid: {
    error: rgba('#F55B7A', 1),
    success: '#6BC796',
    warning: '#F49301',
    info: '#6AD5E5',
    accent: '#FF4496',
    selection: rgba('#6AD5E5', 1),
  },

  tint: {
    primary: '#333D6C',
    secondary: '#686F92',
    tertiary: '#8E93AF',
    action: '#333D6C',
    selection: '#20A3B6',
    highlight: '#20A3B6',
    success: '#399875',
    error: '#E61937',
    errorDark: '#F07587',
    warning: '#D48002',
    onCustom: '#FFFFFF',
    onPrimary: '#283257',
    onSolid: '#FFFFFF',
    onError: '#EB4860',
    onSuccess: '#297048',
    onWarning: '#AA6600',
    onInfo: '#177482',
    primaryDark: '#FFFFFF',
    secondaryDark: '#ACB2C4',
    tertiaryDark: '#8E93AF',
    actionDark: '#FFFFFF',
  },

  state: {
    active: '#333D6C',
    activeBorder: '#CFD3E8',
    onActive: '#333D6C',
    hover: rgba('#333D6C', 0.05),
    hoverBorder: rgba('#333D6C', 0.5),
    hoverBorderDark: rgba('#FFFFFF', 0.5),
    focused: rgba('#333D6C', 0.12),
    ripple: rgba('#333D6C', 0.16),
    activeBorderDark: '#596082',
    activeDark: '#FFFFFF',
    hoverDark: rgba('#FFFFFF', 0.08),
    focusedDark: rgba('#FFFFFF', 0.16),
    rippleDark: rgba('#FFFFFF', 0.2),
    selected: '#E9F9FB',
  },

  border: {
    outline: '#D9DAE3',
    outlineDark: '#596082',
    regular: rgba('#333D6C', 0.05),
    emphasis: rgba('#333D6C', 0.15),
    regularDark: rgba('#FFFFFF', 0.1),
    emphasisDark: rgba('#FFFFFF', 0.2),
    error: rgba('#E61937', 0.2),
    errorDark: rgba('#E61937', 0.2),
  },

  gradient: {
    purple: ['#3A307B', '#5C4CBF', '#3A307B'],
    blue: ['#34C6F4', '#2F2BF5'],
    cyan: ['#6AD5E5', '#8EE4F1', '#6AD5E5'],
    green: ['#8BFCD0', '#4BC370'],
    yellow: ['#F4E334', '#F58D2B'],
    orange: ['#F49034', '#F52B2B'],
    primary: ['#20A3B6', '#6AD5E5'],
    secondary: ['#7481BE', '#333D6C'],
    accent: ['#FF4496', '#EB0055'],
    health: ['#6AE59F', '#EB0055'],
    light: [rgba('#FFFFFF', 100), rgba('#FFFFFF', 0)],
    dark: [rgba('#313A67', 100), rgba('#313A67', 0)],
    loader: ['rgba(140, 240, 255, 0.25)', '#8CF0FF', '#8CF0FF', 'rgba(140, 240, 255, 0.5)'],
  },

  system: {
    error: {
      90: '#FFF3F5',
      70: '#F07587',
      60: '#F55B7A',
      50: '#E61937',
      30: '#B20040',
    },
    success: {
      90: '#EDF7F1',
      60: '#6BC796',
      40: '#399875',
      30: '#297048',
    },
    warning: {
      90: '#FEF5E7',
      50: '#F49301',
      40: '#D48002',
      30: '#AA6600',
    },
  },

  elevation: {
    1: rgba('#333D6C', 0.16),
    2: rgba('#333D6C', 0.24),
    3: rgba('#333D6C', 0.48),
  },

  custom01: {
    90: '#EDF1FC',
    50: '#5F88E8',
  },
  custom02: {
    90: '#DEECEE',
    50: '#258090',
  },
  custom03: {
    90: '#E5F0DD',
    50: '#53981E',
  },
  custom04: {
    90: '#E9FBF1',
    50: '#6AE59F',
  },
  custom05: {
    90: '#F4EDDB',
    50: '#B3850F',
  },
  custom06: {
    90: '#FBF5E9',
    50: '#E5BD6A',
  },
  custom07: {
    90: '#F7E8DF',
    50: '#D16C33',
  },
  custom08: {
    90: '#F4E4DF',
    50: '#B54C28',
  },
  custom09: {
    90: '#F2E1E4',
    50: '#AA344A',
  },
  custom10: {
    90: '#FFE5F0',
    50: '#D1055E',
  },
  custom11: {
    90: '#F8E7F1',
    50: '#CE61A3',
  },
  custom12: {
    90: '#F2E8F4',
    50: '#AA68B4',
  },
  custom13: {
    90: '#ECE7F4',
    50: '#835CB3',
  },
  custom14: {
    90: '#EDE7E8',
    50: '#4B0F1B',
  },
  custom15: {
    90: '#E9E9F1',
    50: '#696FA4',
  },
  custom16: {
    90: '#EEF3F9',
    50: '#4A76B1',
  },
  custom17: {
    90: '#FCF2D9',
    50: '#ECAB05',
  },
  custom18: {
    90: '#F2F7E2',
    50: '#ABCB3D',
  },
} as const;

export type TThemePalette = typeof colors;
export default colors;
